import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { 
  Phone, 
  Plus, 
  Trash2, 
  Edit2,
  Signal,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Network logos
const MTN_LOGO = "https://upload.wikimedia.org/wikipedia/commons/thumb/9/93/New-mtn-logo.jpg/1200px-New-mtn-logo.jpg";
const ORANGE_LOGO = "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c8/Orange_logo.svg/1200px-Orange_logo.svg.png";

export const PaymentSources = () => {
  const { merchant } = useAuth();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newSource, setNewSource] = useState({
    network: 'MTN',
    phone_number: '',
    label: ''
  });

  useEffect(() => {
    fetchSources();
  }, []);

  const fetchSources = async () => {
    try {
      const res = await axios.get(`${API}/merchant/payment-sources`, { withCredentials: true });
      setSources(res.data);
    } catch (err) {
      console.error('Error fetching payment sources:', err);
      toast.error('Failed to load payment sources');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSource = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const res = await axios.post(`${API}/merchant/payment-sources`, newSource, { withCredentials: true });
      setSources([res.data, ...sources]);
      toast.success('Payment source added successfully!');
      setDialogOpen(false);
      setNewSource({ network: 'MTN', phone_number: '', label: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add payment source');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (source) => {
    const newStatus = source.status === 'active' ? 'disabled' : 'active';
    
    try {
      await axios.patch(`${API}/merchant/payment-sources/${source.source_id}`, 
        { status: newStatus }, 
        { withCredentials: true }
      );
      setSources(sources.map(s => 
        s.source_id === source.source_id ? { ...s, status: newStatus } : s
      ));
      toast.success(`Payment source ${newStatus === 'active' ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Failed to update payment source');
    }
  };

  const handleDelete = async (source) => {
    if (!window.confirm(`Are you sure you want to delete this ${source.network} payment source?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/merchant/payment-sources/${source.source_id}`, { withCredentials: true });
      setSources(sources.filter(s => s.source_id !== source.source_id));
      toast.success('Payment source deleted');
    } catch (err) {
      toast.error('Failed to delete payment source');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div data-testid="payment-sources-page" className="animate-fadeIn">
      <Toaster richColors position="top-right" />
      
      {/* Animation Styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
        .hover-lift { transition: all 0.3s ease; }
        .hover-lift:hover { transform: translateY(-3px); box-shadow: 0 12px 24px -8px rgba(0,0,0,0.12); }
      `}</style>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A1628]">Payment Sources</h1>
          <p className="text-slate-500 mt-1">
            Link your mobile money numbers to receive payments
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white transition-all duration-200 hover:scale-105 active:scale-95" data-testid="add-source-btn">
              <Plus className="w-5 h-5 mr-2" />
              Add Payment Source
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Payment Source</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleAddSource} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="network">Network</Label>
                <Select 
                  value={newSource.network} 
                  onValueChange={(value) => setNewSource({ ...newSource, network: value })}
                >
                  <SelectTrigger data-testid="network-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MTN">
                      <div className="flex items-center gap-2">
                        <img src={MTN_LOGO} alt="MTN" className="w-5 h-5 rounded" />
                        MTN Mobile Money
                      </div>
                    </SelectItem>
                    <SelectItem value="Orange">
                      <div className="flex items-center gap-2">
                        <img src={ORANGE_LOGO} alt="Orange" className="w-5 h-5 rounded" />
                        Orange Money
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone_number">Phone Number</Label>
                <Input
                  id="phone_number"
                  type="tel"
                  value={newSource.phone_number}
                  onChange={(e) => setNewSource({ ...newSource, phone_number: e.target.value })}
                  placeholder="+231 77X XXX XXX"
                  required
                  className="transition-all duration-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  data-testid="phone-input"
                />
                <p className="text-xs text-slate-500">
                  Enter the mobile money number that receives payments
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="label">Label (Optional)</Label>
                <Input
                  id="label"
                  type="text"
                  value={newSource.label}
                  onChange={(e) => setNewSource({ ...newSource, label: e.target.value })}
                  placeholder="e.g., Main Business Account"
                  className="transition-all duration-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  data-testid="label-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid="save-source-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Add Source'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-blue-800 font-medium">How Payment Sources Work</p>
          <p className="text-sm text-blue-600 mt-1">
            When an SMS payment notification is received on any of your linked numbers, 
            FlowLedger will automatically create a transaction and notify you.
          </p>
        </div>
      </div>

      {/* Payment Sources List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      ) : sources.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-slate-100 mx-auto mb-4 flex items-center justify-center">
            <Phone className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-medium text-slate-700 mb-2">No payment sources yet</h3>
          <p className="text-slate-500 mb-4">Add your mobile money numbers to start receiving payments</p>
          <Button 
            onClick={() => setDialogOpen(true)}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Payment Source
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {sources.map((source, idx) => (
            <div 
              key={source.source_id}
              className={`bg-white rounded-xl border p-5 hover-lift cursor-pointer ${
                source.status === 'active' ? 'border-slate-200' : 'border-red-200 opacity-70'
              }`}
              style={{ animationDelay: `${idx * 50}ms` }}
              data-testid={`source-${source.source_id}`}
            >
              <div className="flex items-center gap-4">
                {/* Network Logo */}
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                  source.network === 'MTN' ? 'bg-yellow-50' : 'bg-orange-50'
                }`}>
                  <img 
                    src={source.network === 'MTN' ? MTN_LOGO : ORANGE_LOGO} 
                    alt={source.network} 
                    className="w-10 h-10 object-contain rounded"
                  />
                </div>

                {/* Details */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-slate-800">
                      {source.phone_number}
                    </h3>
                    {source.status === 'active' ? (
                      <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                        <CheckCircle className="w-3 h-3" />
                        Active
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                        <XCircle className="w-3 h-3" />
                        Disabled
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    <span className={`font-medium ${
                      source.network === 'MTN' ? 'text-yellow-600' : 'text-orange-600'
                    }`}>
                      {source.network} {source.network === 'MTN' ? 'Mobile Money' : 'Money'}
                    </span>
                    {source.label && (
                      <span className="text-slate-400">• {source.label}</span>
                    )}
                    <span className="text-slate-400">• Added {formatDate(source.created_at)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleStatus(source)}
                    className={`transition-all duration-200 hover:scale-105 ${
                      source.status === 'active' 
                        ? 'border-red-200 text-red-600 hover:bg-red-50' 
                        : 'border-green-200 text-green-600 hover:bg-green-50'
                    }`}
                  >
                    {source.status === 'active' ? 'Disable' : 'Enable'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(source)}
                    className="text-slate-400 hover:text-red-600 hover:bg-red-50 transition-all duration-200 hover:scale-110"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Network Support Info */}
      <div className="mt-8 grid sm:grid-cols-2 gap-4">
        <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200 hover-lift">
          <div className="flex items-center gap-3 mb-2">
            <img src={MTN_LOGO} alt="MTN" className="w-8 h-8 rounded" />
            <h4 className="font-semibold text-yellow-800">MTN Mobile Money</h4>
          </div>
          <p className="text-sm text-yellow-700">
            Numbers starting with +231 77, +231 88
          </p>
        </div>
        <div className="bg-orange-50 rounded-xl p-4 border border-orange-200 hover-lift">
          <div className="flex items-center gap-3 mb-2">
            <img src={ORANGE_LOGO} alt="Orange" className="w-8 h-8 rounded" />
            <h4 className="font-semibold text-orange-800">Orange Money</h4>
          </div>
          <p className="text-sm text-orange-700">
            Numbers starting with +231 55, +231 88
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSources;
